import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { AppDispatch } from '@/redux/store';
import type { AppNotification } from '@/types/Notification';
import { selectAgents } from '@/redux/agentsSlice';
import {
  fetchMoreNotifications,
  markNotificationsRead,
  selectNotifications,
  selectNotificationsConnection,
  selectNotificationsHasMore,
  selectNotificationsLoadingMore,
  selectNotificationsStatus,
  selectUnreadNotificationCount,
} from '@/redux/notificationsSlice';
import NotificationList from './NotificationList';

/**
 * Mirrors Sidebar's own NavItem: ghost button at the default size (h-9,
 * px-4), full width and left-aligned, with the hover background suppressed so
 * only the text and icon change colour. `data-active` is always false — the
 * bell opens a popover rather than routing anywhere — which selects the same
 * muted/hover colours an inactive nav item gets.
 */
const NAV_ITEM_BUTTON_CLASS =
  'w-full justify-start hover:!bg-transparent hover:text-foreground dark:hover:text-foreground transition-colors duration-150 outline-none data-[active=true]:text-brand dark:data-[active=true]:text-brand data-[active=true]:font-semibold data-[active=false]:text-muted-foreground dark:data-[active=false]:text-muted-foreground data-[active=false]:hover:text-foreground dark:data-[active=false]:hover:text-foreground';

interface NotificationBellProps {
  /**
   * 'sidebar' matches the surrounding NavItems; 'header' matches the mobile
   * header's icon buttons. These need different sizing, so they are a real
   * choice rather than a consequence of whether the label is visible.
   */
  variant?: 'sidebar' | 'header';
  /** Sidebar only: drives the label's slide-in, exactly as NavItem does. */
  isExpanded?: boolean;
  /** Mobile closes the drawer after navigating. */
  onNavigate?: () => void;
  /**
   * Lets the sidebar hold itself open while the panel is up. The popover is
   * portalled out of the rail, so without this the rail collapses the moment
   * the pointer leaves it and the panel is left stranded away from the bell.
   */
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  variant = 'header',
  isExpanded = false,
  onNavigate,
  onOpenChange,
  className,
}) => {
  const isSidebar = variant === 'sidebar';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [isOpen, setIsOpen] = useState(false);

  const agents = useSelector(selectAgents);
  const notifications = useSelector(selectNotifications);
  const unreadCount = useSelector(selectUnreadNotificationCount);
  const status = useSelector(selectNotificationsStatus);
  const loadingMore = useSelector(selectNotificationsLoadingMore);
  const hasMore = useSelector(selectNotificationsHasMore);
  const connection = useSelector(selectNotificationsConnection);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
    // Opening the bell is the "I've seen these" signal, so the dot clears on
    // open rather than on click-through. The reducer updates optimistically.
    if (open && unreadCount > 0) {
      void dispatch(markNotificationsRead(undefined));
    }
  };

  const handleSelect = (notification: AppNotification) => {
    setIsOpen(false);
    onOpenChange?.(false);
    onNavigate?.();

    if (!notification.read_at) {
      void dispatch(markNotificationsRead([notification.id]));
    }

    if (!notification.agent_id) return;

    // ConversationPage resolves the conversation out of the agents slice and
    // redirects away when it isn't there. A notification can easily beat the
    // debounced refetch that would have added it, so only deep-link once the
    // conversation is actually in the store; otherwise land on the tab, where
    // it appears a moment later.
    const isLoaded = agents
      .find(agent => agent.id === notification.agent_id)
      ?.conversations?.some(
        conversation => conversation.id?.toString() === notification.entity_id
      );

    navigate(
      isLoaded && notification.entity_id
        ? `/agents/${notification.agent_id}/conversation/${notification.entity_id}`
        : `/agents/${notification.agent_id}?tab=conversations`
    );
  };

  const badgeLabel = unreadCount > 9 ? '9+' : String(unreadCount);

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size={isSidebar ? 'default' : 'icon'}
          data-active={isSidebar ? false : undefined}
          aria-label={t('notifications.title.translation')}
          className={cn(
            isSidebar
              ? NAV_ITEM_BUTTON_CLASS
              : 'text-foreground hover:text-foreground/80',
            className
          )}
        >
          <span className="relative shrink-0">
            <Bell className={isSidebar ? 'h-7 w-7' : 'h-5 w-5'} />
            {unreadCount > 0 && (
              <span
                className={cn(
                  'absolute -right-1 -top-1 flex items-center justify-center rounded-full bg-destructive text-[10px] font-bold leading-none text-destructive-foreground',
                  unreadCount > 9 ? 'h-4 w-5 px-1' : 'h-4 w-4'
                )}
              >
                {badgeLabel}
              </span>
            )}
          </span>
          {isSidebar && (
            // Always mounted and animated rather than toggled, so the label
            // slides in with the rail instead of popping, like NavItem's.
            <span
              className={cn(
                'ml-2 max-w-[180px] truncate text-lg transition-[opacity,transform] duration-300 ease-in-out',
                isExpanded
                  ? 'translate-x-0 opacity-100'
                  : 'translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100'
              )}
            >
              {t('notifications.title.translation')}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align={isSidebar ? 'start' : 'end'}
        side={isSidebar ? 'right' : 'bottom'}
        sideOffset={8}
        // p-0 lets the rows paint to the edge, so the panel has to clip them
        // back to its rounded glass border — otherwise they square off the
        // corners and cut through the surface's sheen and hairline.
        className="w-[min(22rem,calc(100vw-2rem))] overflow-hidden p-0"
      >
        <div className="flex items-center justify-between border-b border-foreground/10 px-3 py-2">
          <p className="text-sm font-semibold text-foreground">
            {t('notifications.title.translation')}
          </p>
          {unreadCount > 0 && (
            <span className="text-xs text-muted-foreground">
              {t('notifications.unread_count.translation', {
                count: unreadCount,
              })}
            </span>
          )}
        </div>

        <NotificationList
          notifications={notifications}
          status={status}
          loadingMore={loadingMore}
          hasMore={hasMore}
          connection={connection}
          onSelect={handleSelect}
          onLoadMore={() => void dispatch(fetchMoreNotifications())}
        />
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
